/** @format */

import {Box} from '@mui/material';
import {type Theme} from '@mui/material/styles';
import {Trans, useTranslation} from 'react-i18next';

import {shortNumber} from 'helpers/numberHelpers';
import {ratioPercent} from 'features/mediaplan-progress/helpers/ratioPercent';
import {type MetricProgress} from 'features/mediaplan-progress/types';

import {getValueColor} from './getValueColor';
import {RowLabel, RowValue, ForecastLabel, SummaryGrid, sharedClasses} from './sharedStyles';

const PLAN_COLOR = (theme: Theme) => theme.palette.semantic_dark?.surface.onSurface;

interface Props {
  progress: MetricProgress;
  // Direction of "good": false → higher-is-better (clicks…), true → lower-is-better (CPL, Расход…).
  lowerIsBetter: boolean;
  // «План на сегодня» row — shown for additive metrics; for ratio the plan is constant so it's redundant.
  showPlanToDate: boolean;
  // «Прогноз» row — gated by the <7-days rule (shouldShowForecast).
  showForecast: boolean;
  // «План на {{period}}» label (month name or flight date range).
  planPeriodLabel: string;
  // Formats a numeric value with its unit (₽ / % / counts).
  formatValue: (value: number) => string;
}

/**
 * The shared «План на … / на сегодня / Факт / Выполнение / Прогноз» text grid rendered by both the
 * KPI rows (KpiCharts) and the Расход block (SpendSection). Fact/«Выполнение»/forecast colors come
 * from getValueColor so the two never disagree on good/bad.
 */
export function MetricTextBlock({
  progress,
  lowerIsBetter,
  showPlanToDate,
  showForecast,
  planPeriodLabel,
  formatValue,
}: Props) {
  const {t} = useTranslation();
  const {planForPeriod, planToDate, factToDate, forecastAtEnd} = progress;

  // Выполнение = факт / план на сегодня (доля освоенного плана к текущей дате).
  const completionPercent = ratioPercent(factToDate, planToDate);
  const factColor = getValueColor(factToDate, planToDate, lowerIsBetter);
  const forecastColor = getValueColor(forecastAtEnd, planForPeriod, lowerIsBetter);

  return (
    <SummaryGrid>
      <RowLabel>{t('План на {{month}}', {month: planPeriodLabel})}</RowLabel>
      <RowValue sx={{color: PLAN_COLOR}}>{formatValue(planForPeriod)}</RowValue>

      {showPlanToDate && (
        <>
          <RowLabel>
            <Trans>План на сегодня</Trans>
          </RowLabel>
          <RowValue sx={{color: PLAN_COLOR}}>{formatValue(planToDate)}</RowValue>
        </>
      )}

      <RowLabel>
        <Trans>Факт на сегодня</Trans>
      </RowLabel>
      <RowValue sx={{color: factColor}}>{formatValue(factToDate)}</RowValue>

      <RowLabel>
        <Trans>Выполнение</Trans>
      </RowLabel>
      <RowValue sx={{color: factColor}}>{`${shortNumber(completionPercent)}%`}</RowValue>

      {showForecast && (
        <>
          <ForecastLabel>
            <Box
              component='span'
              className={`material-symbols-outlined ${sharedClasses.forecastIcon}`}
            >
              event_upcoming
            </Box>
            {t('Прогноз')}
          </ForecastLabel>
          <RowValue sx={{color: forecastColor}}>{formatValue(forecastAtEnd)}</RowValue>
        </>
      )}
    </SummaryGrid>
  );
}
